import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyBv_4pxPXBKw-TkIPfmNi5miG8Fu1Bf1Ak",
  authDomain: "infoniumorg.firebaseapp.com",
  projectId: "infoniumorg",
  storageBucket: "infoniumorg.firebasestorage.app",
  messagingSenderId: "246305647835",
  appId: "1:246305647835:web:ea695c728364e8654a30fe",
  measurementId: "G-CFC89CEQBM"
};

// VAPID key for web push - Get this from Firebase Console > Cloud Messaging > Web Push certificates
const VAPID_KEY = "BK_I-JAlwVMqUxsHL2rilnB_2us2LJNcZbi2xoBSjRzk3qmVwb03pQ6IKMk0hMOq2P8EWzpPI3OzwJlD5PISsXo";

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize FCM messaging
let messaging: Messaging | null = null;

// Sync state tracking to prevent race conditions
let fcmSyncInProgress = false;

// Safe messaging initialization (only in browser)
const initializeMessaging = (): Messaging | null => {
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      messaging = getMessaging(app);
      return messaging;
    } catch (error) {
      console.error('Failed to initialize Firebase messaging:', error);
      return null;
    }
  }
  return null;
};

// Notification permission utility
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission === 'denied') {
    console.warn('Notifications are blocked by user');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

// Debug function to validate Firebase/FCM setup
export const debugFCMSetup = async (): Promise<{
  isSupported: boolean;
  hasPermission: boolean;
  hasServiceWorker: boolean;
  firebaseConfig: any;
  vapidKey: string;
  errorDetails?: string;
}> => {
  const result = {
    isSupported: false,
    hasPermission: false,
    hasServiceWorker: false,
    firebaseConfig: firebaseConfig,
    vapidKey: VAPID_KEY,
    errorDetails: ''
  };

  try {
    // Check if notifications are supported
    result.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
    
    // Check permission
    result.hasPermission = Notification.permission === 'granted';
    
    // Check service worker
    result.hasServiceWorker = 'serviceWorker' in navigator;
    
    if (!result.isSupported) {
      result.errorDetails = 'Browser does not support notifications or service workers';
    }
    
    console.log('🔍 FCM Debug Info:', result);
    return result;
  } catch (error) {
    result.errorDetails = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ FCM Debug failed:', error);
    return result;
  }
};

// Enhanced FCM token retrieval with better error handling
export const getFCMToken = async (): Promise<string | null> => {
  console.log('🚀 Starting FCM token retrieval...');
  
  const messaging = initializeMessaging();
  if (!messaging) {
    console.error('❌ Firebase messaging not available');
    return null;
  }

  try {
    // Debug setup first
    const debugInfo = await debugFCMSetup();
    console.log('🔍 Debug info:', debugInfo);

    // Check if notification permission is granted
    const permissionGranted = await requestNotificationPermission();
    if (!permissionGranted) {
      console.warn('⚠️ Notification permission not granted');
      return null;
    }

    // Use existing service worker registration (avoid conflict with main SW)
    console.log('📝 Using existing service worker...');
    const registration = await navigator.serviceWorker.ready;
    console.log('✅ Service worker ready:', registration);

    // Get FCM token with enhanced error handling
    console.log('🎫 Requesting FCM token...');
    console.log('🔑 Using VAPID key:', VAPID_KEY.substring(0, 20) + '...');
    
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    if (token) {
      console.log('✅ FCM token obtained successfully:', token.substring(0, 50) + '...');
      
      // Store token for debugging
      localStorage.setItem('fcm_debug_token', token);
      localStorage.setItem('fcm_debug_timestamp', new Date().toISOString());
      
      return token;
    } else {
      console.warn('⚠️ No FCM token available - this could be due to:');
      console.warn('  1. Invalid VAPID key');
      console.warn('  2. Service worker not properly registered');
      console.warn('  3. Missing notification permission');
      console.warn('  4. Firebase project configuration issues');
      return null;
    }
  } catch (error: any) {
    console.error('❌ Failed to get FCM token:', error);
    
    // Enhanced error logging
    if (error.code === 'messaging/token-subscribe-failed') {
      console.error('🚨 FCM Token Subscribe Failed - Common causes:');
      console.error('  1. Invalid VAPID key - check Firebase Console > Cloud Messaging > Web Push certificates');
      console.error('  2. Missing FCM Registration API - enable it in Google Cloud Console');
      console.error('  3. Firebase project misconfiguration');
      console.error('  4. Network connectivity issues');
      
      // Store error for debugging
      localStorage.setItem('fcm_debug_error', JSON.stringify({
        code: error.code,
        message: error.message,
        timestamp: new Date().toISOString()
      }));
    }
    
    return null;
  }
};

// Function to test VAPID key validity
export const testVAPIDKey = async (): Promise<{
  isValid: boolean;
  errorMessage?: string;
}> => {
  try {
    // Basic VAPID key format validation
    if (!VAPID_KEY || VAPID_KEY.length < 80) {
      return {
        isValid: false,
        errorMessage: 'VAPID key is too short or missing'
      };
    }

    // Try to get token as a test
    const token = await getFCMToken();
    return {
      isValid: !!token,
      errorMessage: token ? undefined : 'Could not generate token with this VAPID key'
    };
  } catch (error) {
    return {
      isValid: false,
      errorMessage: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback: (payload: any) => void) => {
  const messaging = initializeMessaging();
  if (!messaging) {
    console.error('Firebase messaging not available for foreground messages');
    return () => {};
  }

  try {
    return onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      callback(payload);
    });
  } catch (error) {
    console.error('Failed to set up foreground message listener:', error);
    return () => {};
  }
};

// Send token to server
export const subscribeToNotifications = async (): Promise<{
  success: boolean;
  token?: string;
  error?: string;
}> => {
  try {
    // Get FCM token
    const token = await getFCMToken();
    if (!token) {
      return { success: false, error: 'Unable to get FCM token' };
    }

    // Send token to server
    const response = await fetch('/api/notifications/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        token,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }),
    });

    if (response.ok) {
      const result = await response.json();
      console.log('Successfully subscribed to notifications:', result);
      
      // Store subscription status
      localStorage.setItem('fcm_subscribed', 'true');
      localStorage.setItem('fcm_token', token);
      localStorage.setItem('fcm_subscribed_at', new Date().toISOString());
      
      return { success: true, token };
    } else {
      const errorData = await response.json();
      console.error('Failed to subscribe to notifications:', errorData);
      return { success: false, error: errorData.message || 'Subscription failed' };
    }
  } catch (error) {
    console.error('Error subscribing to notifications:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Synchronize local subscription state with actual FCM status.
// This is triggered when we detect granted permission but missing local markers.
export const syncSubscriptionState = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator)) return false;

  try {
    // Ensure service worker is ready
    const registration = await navigator.serviceWorker.ready;

    // If user denied permission we consider unsubscribed
    if (Notification.permission !== 'granted') {
      localStorage.removeItem('fcm_subscribed');
      localStorage.removeItem('fcm_token');
      localStorage.removeItem('fcm_subscribed_at');
      return false;
    }

    // Try to retrieve existing FCM token
    const token = await getFCMToken();
    if (token) {
      localStorage.setItem('fcm_subscribed', 'true');
      localStorage.setItem('fcm_token', token);
      if (!localStorage.getItem('fcm_subscribed_at')) {
        localStorage.setItem('fcm_subscribed_at', new Date().toISOString());
      }
      return true;
    }

    // No token retrieved – treat as unsubscribed
    localStorage.removeItem('fcm_subscribed');
    localStorage.removeItem('fcm_token');
    localStorage.removeItem('fcm_subscribed_at');
    return false;
  } catch (error) {
    console.error('❌ Failed to sync subscription state:', error);
    return false;
  }
};

// Check if already subscribed (enhanced persistence check)
export const isSubscribedToNotifications = (): boolean => {
  const subscribed = localStorage.getItem('fcm_subscribed') === 'true';
  const hasToken = !!localStorage.getItem('fcm_token');
  const subscribedAt = localStorage.getItem('fcm_subscribed_at');

  // Quick happy path
  if (subscribed && hasToken) {
    // Validate expiry (90 days instead of 30 to reduce unnecessary prompts)
    if (subscribedAt) {
      const subscribeTime = new Date(subscribedAt).getTime();
      const ninetyDaysAgo = Date.now() - 90 * 24 * 60 * 60 * 1000;
      if (subscribeTime < ninetyDaysAgo) {
        console.log('📅 Subscription older than 90 days – will attempt silent resubscribe');
        syncSubscriptionState();
      }
    }
    return true;
  }

  // If permission already granted but markers missing, attempt silent sync
  if (Notification.permission === 'granted') {
    // Don't await to avoid blocking UI, but check if sync is in progress
    if (!fcmSyncInProgress) {
      fcmSyncInProgress = true;
      syncSubscriptionState().finally(() => {
        fcmSyncInProgress = false;
      });
    }
    // Return false here to show button until sync completes
    return false;
  }

  return false;
};

// Unsubscribe from notifications
export const unsubscribeFromNotifications = async (): Promise<boolean> => {
  try {
    const token = localStorage.getItem('fcm_token');
    if (!token) {
      console.warn('No FCM token found for unsubscription');
      return true; // Consider it successful if no token exists
    }

    const response = await fetch('/api/notifications/unsubscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    if (response.ok) {
      // Clear local storage
      localStorage.removeItem('fcm_subscribed');
      localStorage.removeItem('fcm_token');
      localStorage.removeItem('fcm_subscribed_at');
      
      console.log('Successfully unsubscribed from notifications');
      return true;
    } else {
      console.error('Failed to unsubscribe from notifications');
      return false;
    }
  } catch (error) {
    console.error('Error unsubscribing from notifications:', error);
    return false;
  }
};

// Show local notification (fallback)
export const showLocalNotification = (title: string, body: string, icon?: string) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: icon || '/icons/icon-192x192.png',
      badge: '/icons/icon-192x192.png',
      tag: 'infonium-notification'
    });
  }
};

// Firebase service instance
export { app as firebaseApp };
export default {
  requestNotificationPermission,
  getFCMToken,
  onForegroundMessage,
  subscribeToNotifications,
  unsubscribeFromNotifications,
  isSubscribedToNotifications,
  showLocalNotification
}; 